/**
 * BreathPacer — visual breathing guide.
 *
 * A pulsing circle that expands during the inhale half of the cycle and
 * contracts during the exhale half. Pairs with the Coherence (HRV) heartbeat
 * feature: syncing breath to ~5.5 bpm is the well-documented sweet-spot for
 * resonant breathing / peak HRV / vagal tone.
 *
 * The animation runs on requestAnimationFrame and updates a CSS `transform:
 * scale(...)` (GPU-accelerated, no layout thrash). Pure visual — touches no
 * audio nodes. Caller controls `enabled` and `bpm`; we report nothing back
 * other than via onToggle / onBpmChange.
 *
 * Math: `scale = 0.6 + 0.4 * (0.5 - 0.5 * cos(phase * 2π))` smoothly oscillates
 * 0.6 → 1.0 → 0.6 every cycle, with the natural ease-in-out shape of a cosine
 * (slow at the extremes, fast through the middle — exactly how breathing
 * actually feels).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Wind } from 'lucide-react';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// Curated rates. 5.5 bpm is the resonance frequency cited by HeartMath et al.
// Anything outside 3–8 bpm is hard to sustain and was deliberately excluded.
const RATES = [
  { bpm: 4,   label: '4 bpm · Slow & calming' },
  { bpm: 5,   label: '5 bpm · Deeply relaxed' },
  { bpm: 5.5, label: '5.5 bpm · Peak HRV (resonance)' },
  { bpm: 6,   label: '6 bpm · Balanced' },
  { bpm: 7,   label: '7 bpm · Light & energising' },
];

export const BreathPacer = ({ enabled, onToggle, bpm = 5.5, onBpmChange, introPulse = false }) => {
  const [scale, setScale] = useState(0.6);
  const [phase, setPhase] = useState('Ready');

  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      // Smoothly settle the circle back to neutral when disabled.
      setScale(0.6);
      setPhase('Paused');
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }

    startRef.current = performance.now();
    const periodMs = (60 / bpm) * 1000;

    const tick = (now) => {
      const elapsed = (now - startRef.current) % periodMs;
      const p = elapsed / periodMs; // 0..1 within one cycle
      const s = 0.6 + 0.4 * (0.5 - 0.5 * Math.cos(p * 2 * Math.PI));
      setScale(s);
      setPhase(p < 0.5 ? 'Inhale' : 'Exhale');
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, bpm]);

  return (
    <div className="space-y-3" data-testid="breath-pacer">
      <div className="flex items-center justify-between">
        <label className="text-white text-sm flex items-center gap-2">
          <Wind className="h-4 w-4" />
          Breath Pacer
        </label>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => onToggle?.(v)}
          data-testid="breath-pacer-toggle"
          aria-label="Toggle breath pacer"
        />
      </div>

      {enabled && (
        <>
          {/* Intro pulse — only visible for the first ~5s of an auto-launched
              Coherence preview (URL ?coherence=1). Gives a clear "follow this
              circle" cue to first-time visitors arriving from the landing
              page hero CTA, without nagging users who manually toggle the
              pacer. */}
          {introPulse && (
            <div
              className="text-center text-xs text-cyan-300 font-medium tracking-wide animate-pulse"
              data-testid="breath-pacer-intro-banner"
            >
              ✨ Sync your breath to the circle
            </div>
          )}

          <div className="flex items-center justify-center py-3" aria-live="polite">
            <div className="relative w-36 h-36 flex items-center justify-center">
              {/* Outer glow ring — only visible during the intro pulse window.
                  Uses Tailwind's ping animation for a soft expanding aura. */}
              {introPulse && (
                <>
                  <div
                    className="absolute inset-0 rounded-full bg-cyan-300/20 animate-ping"
                    style={{ animationDuration: '1.6s' }}
                    aria-hidden="true"
                    data-testid="breath-pacer-intro-pulse"
                  />
                  <div
                    className="absolute -inset-2 rounded-full border-2 border-cyan-300/40 animate-pulse"
                    aria-hidden="true"
                  />
                </>
              )}
              {/* The breathing circle. transform-only animation = GPU friendly. */}
              <div
                className="absolute inset-0 rounded-full bg-cyan-300/15 border-2 border-cyan-300/60"
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: 'center',
                  willChange: 'transform',
                }}
                data-testid="breath-pacer-circle"
              />
              {/* Soft inner glow sells the depth without animating extra layers. */}
              <div
                className="absolute inset-3 rounded-full bg-cyan-300/10"
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: 'center',
                }}
                aria-hidden="true"
              />
              <span
                className="relative text-white text-sm font-medium tracking-wide select-none"
                data-testid="breath-pacer-phase"
              >
                {phase}
              </span>
            </div>
          </div>

          <Select
            value={String(bpm)}
            onValueChange={(v) => onBpmChange?.(parseFloat(v))}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="breath-pacer-rate-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RATES.map((r) => (
                <SelectItem key={r.bpm} value={String(r.bpm)}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-white/50 text-xs">
            Sync your breathing to the circle. 5.5 bpm is the resonant frequency
            associated with peak heart-rate variability.
          </div>
        </>
      )}
    </div>
  );
};

export default BreathPacer;
